<template>
  <dashboard-layout title="Year Ahead Reports" subtitle="Generation cost & per-slice breakdown">
    <template #actions>
      <search-box v-model="search" placeholder="Search user / report id…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <loading-state v-if="loading && !generations.length && !stats" label="Loading Year Ahead cost…" />

    <template v-else>
      <!-- Headline numbers from the reportStats/year_ahead rollup -->
      <stat-grid>
        <stat-card label="Avg cost / report" :value="formatCost(avgCost)" icon="dollar" accent>
          <template #sub>full only {{ formatCost(avgCostFull) }} · excl. repairs</template>
        </stat-card>
        <stat-card label="Total spent" :value="formatCost(stats ? stats.totalCostUSD : 0)" icon="creditcard">
          <template #sub>{{ (stats ? stats.count : 0).toLocaleString('en-US') }} generations</template>
        </stat-card>
        <stat-card label="Reports" :value="(stats ? stats.count : 0).toLocaleString('en-US')" icon="filetext">
          <template #sub>{{ stats ? stats.completed : 0 }} complete · {{ stats ? stats.partial : 0 }} partial</template>
        </stat-card>
        <stat-card label="Repairs" :value="(stats ? stats.repairs : 0).toLocaleString('en-US')" icon="sliders">
          <template #sub>partial force-repairs</template>
        </stat-card>
        <stat-card label="Avg duration" :value="formatMs(avgMs)" icon="activity">
          <template #sub v-if="stats && stats.lastAtMs">last {{ formatDate(stats.lastAtMs) }}</template>
        </stat-card>
      </stat-grid>

      <p class="note">
        Headline numbers are the all-time <code>reportStats/year_ahead</code> rollup. The chart, slice
        breakdown and table below reflect the {{ generations.length.toLocaleString('en-US') }} loaded
        generation{{ generations.length === 1 ? '' : 's' }}. Claude only — no Gemini spend.
      </p>

      <div v-if="!generations.length" class="empty-wrap">
        <empty-state title="No generations loaded yet" icon="filetext">
          Rows appear here as the backend writes <code>report_generations</code> for year_ahead.
        </empty-state>
      </div>

      <template v-else>
        <!-- Cost per day -->
        <data-panel title="Cost per day (USD)">
          <div class="chart-box"><canvas ref="dayCanvas"></canvas></div>
        </data-panel>

        <!-- Cost by slice -->
        <div class="dual-grid">
          <data-panel title="Cost by slice">
            <div class="chart-box small"><canvas ref="sliceCanvas"></canvas></div>
          </data-panel>
          <data-panel title="Avg cost per slice (loaded)">
            <div class="slice-bars">
              <div v-for="s in sliceAgg" :key="s.key" class="slice-bar-row">
                <span class="sb-label">{{ sliceLabel(s.key) }}</span>
                <div class="sb-track"><div class="sb-fill" :style="{ width: s.pct + '%', background: sliceColor(s.key) }"></div></div>
                <span class="sb-val">{{ formatCost(s.avg) }}</span>
              </div>
            </div>
          </data-panel>
        </div>

        <!-- Filters -->
        <div class="filters">
          <filter-chips v-model="stateFilter" :options="stateOptions" />
        </div>

        <!-- Per-report table -->
        <data-panel flush>
          <template #title>Generations <span class="muted">({{ filteredGenerations.length.toLocaleString('en-US') }})</span></template>
          <data-table
            :columns="columns"
            :rows="filteredGenerations"
            row-key="id"
            clickable
            :expanded-key="expandedId"
            @row-click="toggleRow"
          >
            <template #cell-created="{ row }">
              <span class="date-cell"><span class="caret" :class="{ open: expandedId === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
            </template>
            <template #cell-user="{ row }">
              <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
              <span v-else class="muted">—</span>
            </template>
            <template #cell-language="{ row }">{{ row.language || '—' }}</template>
            <template #cell-state="{ row }">
              <status-badge :variant="row.state === 'complete' ? 'success' : 'warning'" :label="row.state || '—'" />
              <status-badge v-if="row.partialRepair" variant="info" label="repair" class="repair-tag" />
            </template>
            <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.costUsd) }}</span></template>
            <template #cell-duration="{ row }">{{ formatMs(row.totalMs) }}</template>
            <template #cell-tokens="{ row }">{{ formatTokens(row.inputTokens) }} / {{ formatTokens(row.outputTokens) }}</template>
            <template #cell-failed="{ row }">
              <span v-if="row.slicesFailed.length" class="failed">{{ row.slicesFailed.join(', ') }}</span>
              <span v-else class="muted">—</span>
            </template>
            <template #empty>No generations match your filters</template>

            <template #expanded="{ row }">
              <div class="slice-detail">
                <table class="slice-table">
                  <thead>
                    <tr><th>Slice</th><th>Model</th><th>OK</th><th>Cost</th><th>Duration</th><th>In/Out</th><th>Attempts</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="key in SLICES" :key="key" :class="{ 'slice-missing': !row.slices[key] }">
                      <td class="sl-name">{{ sliceLabel(key) }}</td>
                      <td class="sl-model">{{ modelShort(sliceOf(row, key).model) }}</td>
                      <td>
                        <span v-if="row.slices[key]" :class="sliceOf(row, key).ok ? 'ok-yes' : 'ok-no'">{{ sliceOf(row, key).ok ? '✓' : '✕' }}</span>
                        <span v-else class="muted">—</span>
                      </td>
                      <td>{{ row.slices[key] ? formatCost(num(sliceOf(row, key).costUsd)) : '—' }}</td>
                      <td>{{ row.slices[key] ? formatMs(num(sliceOf(row, key).ms)) : '—' }}</td>
                      <td>{{ row.slices[key] ? (formatTokens(num(sliceOf(row, key).inputTokens)) + ' / ' + formatTokens(num(sliceOf(row, key).outputTokens))) : '—' }}</td>
                      <td>{{ row.slices[key] ? num(sliceOf(row, key).attempts) : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="slice-foot">
                  <span>Report: <code>{{ row.reportId || '—' }}</code></span>
                  <span v-if="row.promptVersion">Prompt: <code>{{ row.promptVersion }}</code></span>
                </div>
              </div>
            </template>
          </data-table>
        </data-panel>
      </template>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseYearAheadService, YEAR_AHEAD_SLICES } from '../services/firebaseYearAheadService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

// season slices share a warm hue; spine/closing (haiku) blue; overview purple.
const SLICE_COLORS = {
  spine: '#22d3ee', overview: '#6c63ff',
  season_1: '#f8bf71', season_2: '#f4a95f', season_3: '#f0934d', season_4: '#eb7d3b',
  closing: '#4ade80'
}

const columns = [
  { key: 'created', label: 'Created', width: '160px' },
  { key: 'user', label: 'User', width: '110px' },
  { key: 'language', label: 'Lang', width: '64px' },
  { key: 'state', label: 'State', width: '130px' },
  { key: 'cost', label: 'Cost', width: '90px' },
  { key: 'duration', label: 'Duration', width: '90px' },
  { key: 'tokens', label: 'Tok in/out', width: '120px' },
  { key: 'failed', label: 'Failed slices' }
]

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'YearAheadReportsDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseYearAheadService()

    const stats = ref(null)
    const generations = ref([])
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const stateFilter = ref('all')
    const expandedId = ref(null)

    const dayCanvas = ref(null)
    const sliceCanvas = ref(null)
    let dayChart = null
    let sliceChart = null

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [s, g] = await Promise.all([service.getStats(), service.getGenerations()])
        stats.value = s
        generations.value = g
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else if (code === 'failed-precondition') error.value = (e && e.message) || 'A Firestore index is required for this query.'
        else error.value = (e && e.message) || 'Failed to load Year Ahead cost.'
      } finally {
        loading.value = false
      }
    }

    const avgCost = computed(() => (stats.value && stats.value.count) ? stats.value.totalCostUSD / stats.value.count : 0)
    const avgCostFull = computed(() => {
      if (!stats.value) return 0
      const full = stats.value.count - stats.value.repairs
      return full > 0 ? stats.value.totalCostUSD / full : 0
    })
    const avgMs = computed(() => (stats.value && stats.value.count) ? stats.value.totalMs / stats.value.count : 0)

    const stateOptions = computed(() => {
      const complete = generations.value.filter(g => g.state === 'complete').length
      const partial = generations.value.filter(g => g.state === 'partial').length
      return [
        { value: 'all', label: 'All', count: generations.value.length },
        { value: 'complete', label: 'Complete', count: complete },
        { value: 'partial', label: 'Partial', count: partial }
      ]
    })

    const filteredGenerations = computed(() => {
      const q = search.value.toLowerCase().trim()
      return generations.value.filter(g => {
        if (stateFilter.value !== 'all' && g.state !== stateFilter.value) return false
        if (q) {
          const hay = `${g.userId} ${g.reportId} ${g.language}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    // Aggregate loaded generations' slices: total + avg cost per slice.
    const sliceAgg = computed(() => {
      const totals = {}
      const counts = {}
      YEAR_AHEAD_SLICES.forEach(k => { totals[k] = 0; counts[k] = 0 })
      generations.value.forEach(g => {
        YEAR_AHEAD_SLICES.forEach(k => {
          const sl = g.slices && g.slices[k]
          if (sl) { totals[k] += num(sl.costUsd); counts[k] += 1 }
        })
      })
      const rows = YEAR_AHEAD_SLICES.map(k => ({ key: k, total: totals[k], avg: counts[k] ? totals[k] / counts[k] : 0 }))
      const maxAvg = Math.max(...rows.map(r => r.avg), 0.0001)
      return rows.map(r => ({ ...r, pct: Math.round((r.avg / maxAvg) * 100) }))
    })

    // date(YYYY-MM-DD) -> summed costUsd, ascending.
    const daySeries = computed(() => {
      const map = {}
      generations.value.forEach(g => {
        if (!g.createdAtMs) return
        const key = new Date(g.createdAtMs).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
        map[key] = (map[key] || 0) + g.costUsd
      })
      return Object.keys(map).sort().map(date => ({ date, cost: map[date] }))
    })

    const destroyCharts = () => {
      [dayChart, sliceChart].forEach(c => c && c.destroy())
      dayChart = sliceChart = null
    }

    const legendDark = { labels: { color: '#cfcfe1', padding: 12, font: { size: 12 }, usePointStyle: true } }
    const tooltipDark = { backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3', borderColor: '#2c2d42', borderWidth: 1, padding: 12 }
    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 }, maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }

    const renderCharts = () => {
      destroyCharts()
      if (!generations.value.length) return

      if (dayCanvas.value) {
        const series = daySeries.value
        dayChart = new Chart(dayCanvas.value.getContext('2d'), {
          type: 'bar',
          data: {
            labels: series.map(d => d.date),
            datasets: [{ label: 'Cost (USD)', data: series.map(d => +d.cost.toFixed(6)), backgroundColor: 'rgba(108,99,255,0.65)', borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4 }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { ...tooltipDark, callbacks: { label: c => ' $' + (c.parsed.y || 0).toFixed(4) } } },
            scales: { ...baseScales, y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } } }
          }
        })
      }

      if (sliceCanvas.value) {
        const agg = sliceAgg.value.filter(s => s.total > 0)
        sliceChart = new Chart(sliceCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: {
            labels: agg.map(s => sliceLabel(s.key)),
            datasets: [{ data: agg.map(s => +s.total.toFixed(6)), backgroundColor: agg.map(s => sliceColor(s.key)), borderColor: '#1a1b2e', borderWidth: 2 }]
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutout: '60%',
            plugins: { legend: { position: 'bottom', ...legendDark }, tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } } }
          }
        })
      }
    }

    const toggleRow = (row) => { expandedId.value = expandedId.value === row.id ? null : row.id }
    const sliceOf = (row, key) => (row.slices && row.slices[key]) || {}

    const sliceLabel = (k) => k ? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'
    const sliceColor = (k) => SLICE_COLORS[k] || '#8b8b9e'
    const modelShort = (m) => !m ? '—' : m.replace('claude-', '').replace(/-\d{8}$/, '')
    const truncate = (t, max) => !t ? '—' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => {
      const v = n || 0
      if (v < 1000) return String(v)
      if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'
      return (v / 1e6).toFixed(2) + 'M'
    }
    const formatMs = ms => {
      const v = Math.round(ms || 0)
      if (v < 1000) return v + 'ms'
      if (v < 60000) return (v / 1000).toFixed(1) + 's'
      const m = Math.floor(v / 60000)
      const s = Math.round((v % 60000) / 1000)
      return s ? `${m}m ${s}s` : `${m}m`
    }
    const formatDate = ms => {
      if (!ms) return '—'
      return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
    const openUser = (uid, event) => {
      if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank')
      else router.push(`/user/${uid}`)
    }

    // Redraw after the DOM (incl. chart panels that first mount when data arrives) is patched.
    watch(generations, () => renderCharts(), { flush: 'post' })

    onMounted(load)
    onBeforeUnmount(destroyCharts)

    return {
      stats, generations, loading, error, search, stateFilter, expandedId,
      columns, SLICES: YEAR_AHEAD_SLICES,
      avgCost, avgCostFull, avgMs, stateOptions, filteredGenerations, sliceAgg,
      dayCanvas, sliceCanvas,
      load, toggleRow, sliceOf, num,
      sliceLabel, sliceColor, modelShort, truncate, formatCost, formatTokens, formatMs, formatDate, openUser
    }
  }
}
</script>

<style scoped>
.error-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: var(--warning);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}
.refresh-btn {
  padding: 9px 14px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }

.note { margin: 0; font-size: 12px; color: var(--text-secondary); }
.note code { font-size: 11px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }
.empty-wrap code { font-size: 12px; background: var(--field-bg); padding: 1px 5px; border-radius: 4px; }

.chart-box { height: 300px; position: relative; }
.chart-box.small { height: 280px; }
.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }

.slice-bars { display: flex; flex-direction: column; gap: 10px; }
.slice-bar-row { display: flex; align-items: center; gap: 10px; }
.sb-label { width: 92px; flex-shrink: 0; font-size: 12.5px; color: var(--text-primary); }
.sb-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.sb-fill { height: 100%; border-radius: 4px; }
.sb-val { width: 74px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }

.filters { display: flex; gap: 12px; flex-wrap: wrap; }

.muted { color: var(--text-secondary); font-weight: 400; }
.strong { font-weight: 600; }
.date-cell { white-space: nowrap; }
.caret { display: inline-block; width: 11px; color: var(--text-secondary); transition: transform 0.18s ease; }
.caret.open { transform: rotate(90deg); color: var(--primary-color); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.repair-tag { margin-left: 6px; }
.failed { color: var(--danger); font-size: 12px; }

/* Expanded per-slice detail */
.slice-detail { padding: 14px 16px 16px; }
.slice-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.slice-table th, .slice-table td { text-align: left; padding: 7px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; }
.slice-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.slice-table td { color: var(--text-primary); }
.slice-table tr:last-child td { border-bottom: none; }
.sl-name { font-weight: 600; }
.sl-model { color: var(--text-secondary); font-family: monospace; font-size: 11.5px; }
.slice-missing td { opacity: 0.45; }
.ok-yes { color: var(--success); font-weight: 700; }
.ok-no { color: var(--danger); font-weight: 700; }
.slice-foot { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.slice-foot code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }
</style>
